/**
 * Sync Channel Service
 *
 * Uses BroadcastChannel API to sync data across browser tabs.
 * When data changes in one tab, other tabs are notified to refresh.
 */

type SyncMessageType = 'items' | 'config';

type SyncCallback = () => void;

let channel: BroadcastChannel | null = null;
let listeners: SyncCallback[] = [];

/**
 * Initialize the sync channel
 */
function ensureChannel(): BroadcastChannel {
  if (!channel) {
    channel = new BroadcastChannel('personal-insights-sync');
    channel.onmessage = () => {
      // Notify all listeners when we receive a sync message from another tab
      listeners.forEach((callback) => callback());
    };
  }
  return channel;
}

/**
 * Broadcast a change to other tabs
 *
 * @param type - Type of data that changed ('items' or 'config')
 */
export function broadcastChange(type: SyncMessageType = 'items'): void {
  try {
    const ch = ensureChannel();
    ch.postMessage({ type, timestamp: Date.now() });
  } catch (error) {
    // BroadcastChannel may not be available in all browsers
    console.warn('BroadcastChannel not available:', error);
  }
}

/**
 * Listen for changes from other tabs
 *
 * @param callback - Function to call when another tab makes changes
 * @returns Cleanup function to remove the listener
 */
export function onRemoteChange(callback: SyncCallback): () => void {
  ensureChannel();
  listeners.push(callback);

  return () => {
    listeners = listeners.filter((cb) => cb !== callback);
  };
}

/**
 * Close the channel (for cleanup)
 */
export function closeChannel(): void {
  if (channel) {
    channel.close();
    channel = null;
    listeners = [];
  }
}
